import { createFileRoute, Link } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";

import { SiteFooter, SiteNav } from "@/components/SiteChrome";
import { listPublishedEssays } from "@/lib/essays.functions";

const publishedEssaysQuery = {
  queryKey: ["essays", "published"] as const,
  queryFn: () => listPublishedEssays(),
};

export const Route = createFileRoute("/garden")({
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(publishedEssaysQuery);
  },
  head: () => ({
    meta: [
      { title: "Digital Garden — Akram Fortas" },
      {
        name: "description",
        content:
          "A slow-growing collection of essays and notes on marketing, business, experiments, travel, and life by Akram Fortas.",
      },
      { property: "og:title", content: "Digital Garden — Akram Fortas" },
      { property: "og:description", content: "Notes that are still growing." },
      { property: "og:url", content: "https://akramfortas.com/garden" },
    ],
    links: [{ rel: "canonical", href: "https://akramfortas.com/garden" }],
  }),
  errorComponent: ({ error }) => (
    <div className="min-h-screen bg-background p-10 text-foreground">
      <p>Couldn't load the garden: {error.message}</p>
    </div>
  ),
  notFoundComponent: () => <p className="p-10">Not found.</p>,
  component: GardenPage,
});

const categories = [
  { name: "Marketing", desc: "Positioning, attention, and the long, quiet work of being trusted." },
  { name: "Business", desc: "Building small things that don't require a war to survive." },
  { name: "Experiments", desc: "Things I tried. Some worked. Most didn't. All taught me something." },
  { name: "Travel", desc: "Notes from cafés, trains, and the kind of mornings you only get far from home." },
  { name: "Life", desc: "Personal essays. Slower in tone. Honest by default." },
];

function GardenPage() {
  const { data: essays } = useSuspenseQuery(publishedEssaysQuery);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />

      <main className="container-prose pt-20 pb-16 md:pt-28">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          The garden
        </p>
        <h1 className="mt-6 font-serif text-5xl text-balance text-foreground md:text-6xl">
          Notes that are{" "}
          <span className="italic text-[color:var(--color-leaf)]">still growing</span>.
        </h1>
        <p className="mt-6 max-w-2xl text-muted-foreground text-pretty">
          This is a digital garden, not a blog. Some notes are seeds, some are saplings, some
          might one day be trees. Nothing here is finished — that's the whole idea.
        </p>

        {essays.length > 0 && (
          <section className="mt-16">
            <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Recent notes
            </h2>
            <ul className="mt-6 divide-y hairline border-y">
              {essays.map((e) => (
                <li key={e.id}>
                  <Link
                    to="/garden/$slug"
                    params={{ slug: e.slug }}
                    className="group flex flex-col gap-1 py-6 sm:flex-row sm:items-baseline sm:justify-between sm:gap-8"
                  >
                    <div>
                      <h3 className="font-serif text-2xl text-foreground transition-colors group-hover:text-[color:var(--color-leaf)]">
                        {e.title}
                      </h3>
                      {e.subtitle && (
                        <p className="mt-1 text-sm text-muted-foreground text-pretty">
                          {e.subtitle}
                        </p>
                      )}
                    </div>
                    <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                      {e.category ?? "Note"}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-20">
          <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Patches
          </h2>
          <ul className="mt-6 grid gap-px bg-border border hairline sm:grid-cols-2">
            {categories.map((c) => (
              <li
                key={c.name}
                className="group bg-background p-8 transition-colors hover:bg-secondary/60"
              >
                <div className="flex items-baseline justify-between gap-4">
                  <h3 className="font-serif text-3xl text-foreground">{c.name}</h3>
                  <span className="font-mono text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    🌱 Growing
                  </span>
                </div>
                <p className="mt-3 text-muted-foreground text-pretty">{c.desc}</p>
              </li>
            ))}
          </ul>
        </section>

        {essays.length === 0 && (
          <section className="mt-20 border-t hairline pt-10">
            <p className="font-serif text-2xl text-foreground italic">
              No essays here yet. The soil is being prepared.
            </p>
            <p className="mt-3 text-muted-foreground">
              Come back in a season. Or follow along on{" "}
              <a
                className="link-quiet underline underline-offset-4"
                href="https://x.com/fopreneur"
                target="_blank"
                rel="noreferrer noopener"
              >
                X
              </a>{" "}
              while the first notes take root.
            </p>
          </section>
        )}

        <p className="mt-20 text-sm text-muted-foreground">
          ←{" "}
          <Link to="/" className="link-quiet underline underline-offset-4">
            Back home
          </Link>
        </p>
      </main>

      <SiteFooter />
    </div>
  );
}
